import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
  })
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours > 0) {
    return `${hours}h ${mins}m`
  }
  return `${mins}m`
}

export function calculateTrend(current: number, previous: number): { trend: 'up' | 'down' | 'neutral'; value: string } {
  if (previous === 0) return { trend: 'neutral', value: '0%' }
  
  const diff = current - previous
  const percent = ((diff / previous) * 100)
  
  if (percent > 0) {
    return { trend: 'up', value: `+${percent.toFixed(1)}%` }
  } else if (percent < 0) {
    return { trend: 'down', value: `${percent.toFixed(1)}%` }
  }
  return { trend: 'neutral', value: '0%' }
}

export function getActivityColor(level: 'low' | 'medium' | 'high' | 'very_high'): string {
  const colors = {
    low: 'text-gray-500 bg-gray-100',
    medium: 'text-yellow-600 bg-yellow-100',
    high: 'text-green-600 bg-green-100',
    very_high: 'text-blue-600 bg-blue-100',
  }
  return colors[level] || colors.low
}

export function getSleepQualityColor(quality: string): string {
  const colors = {
    'Poor': 'text-red-600 bg-red-100',
    'Fair': 'text-yellow-600 bg-yellow-100',
    'Good': 'text-green-600 bg-green-100',
    'Excellent': 'text-blue-600 bg-blue-100',
  }
  return colors[quality as keyof typeof colors] || colors.Poor
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}
